import React from "react";
import dayjs from "dayjs";
import { Button } from "../ui";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetSingleApplication } from "@/hooks/process/use-incoming";

interface ViewSubmittedFormsModalProps {
  isOpen: boolean;
  onClose: () => void;
  applicantProcessId: string;
  applicationType?: "pending" | "completed" | "disabled" | "processed";
}

const formatTimestamp = (value?: string | null) =>
  value ? dayjs(value).format("MMM D, YYYY, h:mm A") : "-";

const ViewSubmittedFormsModal: React.FC<ViewSubmittedFormsModalProps> = ({
  isOpen,
  onClose,
  applicantProcessId,
  applicationType = "pending",
}) => {
  const { t } = useTranslation();

  const { data, isLoading } = useGetSingleApplication(
    isOpen ? applicantProcessId : "",
    applicationType
  );

  const completedForms = data?.data?.completedForms ?? [];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:rounded-md max-w-[720px]">
        <DialogHeader>
          <DialogTitle className="text-center text-lg sm:text-xl font-semibold">
            {t("processManagement.submittedForms")}
          </DialogTitle>
          <DialogDescription>
            {t("processManagement.viewSubmittedFormsDescription")}
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="space-y-3 p-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center space-x-4">
                  <Skeleton className="h-4 w-8" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-32" />
                </div>
              ))}
            </div>
          ) : completedForms.length > 0 ? (
            <table className="w-full bg-white table-auto">
              <thead className="bg-subprimary text-primary font-normal text-sm sm:text-base rounded-t-lg">
                <tr>
                  <th className="main-th p-2">#</th>
                  <th className="main-th p-2">
                    {t("processManagement.formName")}
                  </th>
                  <th className="main-th p-2">
                    {t("processManagement.submittedAt")}
                  </th>
                  <th className="main-th p-2">
                    {t("processManagement.reviewedAt")}
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-600 text-sm sm:text-base font-light">
                {completedForms.map((form: any, index: number) => (
                  <tr
                    key={`${form.id}-${form.completedAt ?? index}`}
                    className="border-b border-gray-200 hover:bg-gray-100"
                  >
                    <td className="main-td p-2">{form.order ?? index + 1}</td>
                    <td className="main-td p-2">{form.formName || "-"}</td>
                    <td className="main-td p-2">
                      {formatTimestamp(form.submittedAt)}
                    </td>
                    <td className="main-td p-2">
                      {formatTimestamp(form.completedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-center text-gray-600 py-8">
              {t("processManagement.noSubmittedFormsYet")}
            </p>
          )}
        </div>
        <div className="p-4 flex justify-end">
          <Button variant="secondary" onClick={onClose}>
            {t("processManagement.close")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ViewSubmittedFormsModal;
